import { CSSProperties, isValidElement, useState } from 'react'
import DataSet from './DataSet'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'

const camelCaseToSentenceCase = (str: string): string => {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (match) => match.toUpperCase())
    .trim()
}

const toRenderable = (object: { [key: string]: any }, sentenceCase: boolean = true) =>
  Object.entries(object).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [sentenceCase ? camelCaseToSentenceCase(key) : key]: isValidElement(value) ? value : String(value),
    }),
    {},
  )

export interface RecursiveDataSetProps {
  object: { [key: string]: any } | string | number
  style?: CSSProperties
  sentenceCase?: boolean
  depth?: number
}

const CollapsibleSection = ({
  title,
  children,
  depth = 0,
}: {
  title: string
  children: React.ReactNode
  depth?: number
}) => {
  const [expanded, setExpanded] = useState(true)

  // Calculate the offset to align content with header text
  // IconButton has 2px padding + icon width (~20px) + 4px margin = ~26px
  const getContentOffset = () => {
    return '26px' // Approximate width of icon button + margin
  }

  return (
    <Box sx={{ mt: depth === 0 ? 2 : 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover',
            borderRadius: 1,
          },
          p: 0.5,
          ml: -0.5,
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton
          size="small"
          sx={{
            padding: '2px',
            marginRight: '4px',
          }}
        >
          {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ ml: getContentOffset() }}>{children}</Box>
      </Collapse>
    </Box>
  )
}

export const RecursiveDataSet = ({ object, style, sentenceCase = false, depth = 0 }: RecursiveDataSetProps) => {
  if (typeof object !== 'object' || object === null || isValidElement(object)) {
    // Treat React elements like primitives
    return (
      <div style={style}>
        <DataSet values={{ '': isValidElement(object) ? object : String(object) }} />
      </div>
    )
  }

  const entries = Object.entries(object)

  const { flat, objects, arrays } = entries.reduce<{
    flat: { [key: string]: any }
    objects: Array<[string, any]>
    arrays: Array<[string, Array<any>]>
  }>(
    (acc, [key, value]) => {
      if (isValidElement(value)) {
        acc.flat[key] = value
      } else if (Array.isArray(value)) {
        acc.arrays.push([key, value])
      } else if (value && typeof value === 'object') {
        acc.objects.push([key, value])
      } else {
        acc.flat[key] = value
      }
      return acc
    },
    { flat: {}, objects: [], arrays: [] },
  )

  return (
    <div style={style}>
      {/* Render flat values first */}
      {Object.keys(flat).length > 0 && <DataSet values={toRenderable(flat, sentenceCase)} />}

      {/* Render nested objects with collapsible sections */}
      {objects.map(([key, value], ind) => (
        <CollapsibleSection
          key={`${key}-${ind}`}
          title={sentenceCase ? camelCaseToSentenceCase(key) : key}
          depth={depth}
        >
          <RecursiveDataSet object={value} sentenceCase={sentenceCase} depth={depth + 1} />
        </CollapsibleSection>
      ))}

      {/* Render arrays with collapsible sections */}
      {arrays.map(([key, value], ind) => (
        <CollapsibleSection
          key={`${key}-${ind}`}
          title={sentenceCase ? camelCaseToSentenceCase(key) : key}
          depth={depth}
        >
          {value.map((item, i) => (
            <CollapsibleSection key={`${key}-item-${i}`} title={`Item ${i + 1}`} depth={depth + 1}>
              <RecursiveDataSet object={item} sentenceCase={sentenceCase} depth={depth + 2} />
            </CollapsibleSection>
          ))}
        </CollapsibleSection>
      ))}
    </div>
  )
}
